import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePicker } from 'antd';
import { Activity, Power, ScrollText, Wifi, PencilLine } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { dayjs } from '@/lib/dayjs';
import {
  getCommandLogs, getRelayEvents, getStatusEvents, getUptimeSummary, getBoardAuditLogs,
  type ActivityRange, type ActivityCustom, type CommandLogEntry, type RelayEventEntry, type StatusEventEntry,
  type BoardAuditEntry,
} from '@/features/boards/api';

const RANGES: { key: ActivityRange; label: string }[] = [
  { key: '24h', label: '24 ชม.' },
  { key: '7d', label: '7 วัน' },
  { key: '30d', label: '30 วัน' },
  { key: 'custom', label: 'กำหนดเอง' },
];

const pillWrap: React.CSSProperties = {
  display: 'inline-flex', gap: 4, background: 'var(--bg-input)',
  padding: 3, borderRadius: 9, border: '1px solid var(--border-color)',
};
const pill = (active: boolean): React.CSSProperties => ({
  padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
  background: active ? 'var(--cyan)' : 'transparent', color: active ? '#001' : 'var(--dim)',
  fontWeight: active ? 700 : 500, fontSize: 12,
});
const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16,
};
const sectionTitle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase',
  letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
};
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '6px 10px 8px', fontWeight: 600, whiteSpace: 'nowrap',
  // Keep the header visible while the body scrolls.
  position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1,
};
// Scrollable body wrapper so a long history doesn't stretch the page.
const scrollBox: React.CSSProperties = { maxHeight: 320, overflowY: 'auto', overflowX: 'auto' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: 12.5, color: 'var(--text)', verticalAlign: 'top' };
const empty: React.CSSProperties = { padding: 24, textAlign: 'center', color: 'var(--dim)', fontSize: 12.5 };

function dur(s: number): string {
  if (s < 60) return `${s} วินาที`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} นาที`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ชม. ${m % 60} นาที`;
  const d = Math.floor(h / 24);
  return `${d} วัน ${h % 24} ชม.`;
}
const fmt = (iso: string) => dayjs(iso).format('DD/MM/YY HH:mm:ss');
const countLabel = (rows?: unknown[]) =>
  rows && rows.length ? <span style={{ color: 'var(--dim2)', fontWeight: 600 }}>({rows.length})</span> : null;

const ACTION_LABEL: Record<string, string> = {
  relay_override: 'สั่งควบคุมมือ',
  relay_config: 'ตั้งค่าอัตโนมัติ',
  relay_pulse: 'สั่งเปิดชั่วคราว',
  fan: 'ควบคุมพัดลม',
};
const MODE_LABEL: Record<string, string> = {
  none: 'ปิดอัตโนมัติ', temp: 'อุณหภูมิ', schedule: 'ตั้งเวลา', countdown: 'นับถอยหลัง',
  cyclic: 'วนรอบ', sensor: 'เซ็นเซอร์', always_on: 'เปิดค้าง', always_off: 'ปิดค้าง', calendar: 'ปฏิทิน',
};
function sourceLabel(s: string | null): string {
  if (!s) return '—';
  if (s === 'manual_on') return 'สั่งมือ (เปิด)';
  if (s === 'manual_off') return 'สั่งมือ (ปิด)';
  if (s === 'countdown') return 'นับถอยหลัง';
  return MODE_LABEL[s] ?? s;
}
function commandSummary(c: CommandLogEntry): string {
  const p = c.params ?? {};
  if (c.action === 'relay_override') return `→ ${p.override}`;
  if (c.action === 'relay_pulse') return `${p.seconds} วินาที`;
  if (c.action === 'fan') return `โหมด ${p.mode}`;
  if (c.action === 'relay_config') return typeof p.autoMode === 'string' ? (MODE_LABEL[p.autoMode] ?? String(p.autoMode)) : 'อัปเดตค่า';
  return '';
}

function Badge({ on }: { on: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 700,
      background: on ? 'rgba(34,197,94,0.14)' : 'rgba(148,163,184,0.14)',
      color: on ? '#22c55e' : 'var(--dim)',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: on ? '#22c55e' : 'var(--dim)' }} />
      {on ? 'เปิด' : 'ปิด'}
    </span>
  );
}

export function ActivityHistory({ boardId }: { boardId: number }) {
  const [range, setRange] = useState<ActivityRange>('7d');
  // Stable Dayjs objects so the RangePicker doesn't reset mid-selection.
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(1, 'day'), dayjs()]);
  const [cFrom, cTo] = customRange;

  const customValid = range !== 'custom'
    || (cTo.isAfter(cFrom) && cTo.diff(cFrom, 'day') <= 90);
  const custom: ActivityCustom | undefined =
    range === 'custom' && customValid
      ? { from: cFrom.toISOString(), to: cTo.toISOString() }
      : undefined;
  const enabled = range !== 'custom' || customValid;
  // keys that force a refetch when the custom window moves
  const key = range === 'custom' ? `${custom?.from}_${custom?.to}` : range;

  const uptime = useQuery({
    queryKey: ['board', boardId, 'uptime', key],
    queryFn: () => getUptimeSummary(boardId, range, custom),
    refetchInterval: 30_000, enabled,
  });
  const statusEvents = useQuery({
    queryKey: ['board', boardId, 'status-events', key],
    queryFn: () => getStatusEvents(boardId, range, custom),
    refetchInterval: 30_000, enabled,
  });
  const relayEvents = useQuery({
    queryKey: ['board', boardId, 'relay-events', key],
    queryFn: () => getRelayEvents(boardId, range, custom),
    refetchInterval: 30_000, enabled,
  });
  const commands = useQuery({
    queryKey: ['board', boardId, 'command-logs', key],
    queryFn: () => getCommandLogs(boardId, range, custom),
    refetchInterval: 30_000, enabled,
  });
  // Settings-change audit is not time-range scoped — always the latest 100.
  const audit = useQuery({
    queryKey: ['board', boardId, 'audit-logs'],
    queryFn: () => getBoardAuditLogs(boardId),
    refetchInterval: 30_000,
  });

  const u = uptime.data;

  return (
    <section className="fade-in-up" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* header + range */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ ...sectionTitle, marginBottom: 0 }}>
          <Activity size={14} color="var(--cyan)" /> ประวัติการทำงาน
        </div>
        <div style={pillWrap}>
          {RANGES.map((r) => (
            <button key={r.key} style={pill(range === r.key)} onClick={() => setRange(r.key)}>{r.label}</button>
          ))}
        </div>
      </div>

      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            value={customRange}
            onChange={(v) => { if (v && v[0] && v[1]) setCustomRange([v[0], v[1]]); }}
            disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
            allowClear={false}
            placeholder={['เริ่มต้น', 'สิ้นสุด']}
          />
          {!customValid && <span style={{ color: '#ef4444', fontSize: 11 }}>เลือกช่วง 1–90 วัน (เริ่มก่อนสิ้นสุด)</span>}
        </div>
      )}

      {/* uptime summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>สถานะปัจจุบัน</div>
          <div style={{ marginTop: 8 }}>
            {u?.current == null ? (
              <span style={{ color: 'var(--dim)', fontSize: 15, fontWeight: 700 }}>—</span>
            ) : (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 16, fontWeight: 800,
                color: u.current === 'online' ? '#22c55e' : '#ef4444',
              }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: u.current === 'online' ? '#22c55e' : '#ef4444', boxShadow: `0 0 7px ${u.current === 'online' ? '#22c55e' : '#ef4444'}` }} />
                {u.current === 'online' ? 'ออนไลน์' : 'ออฟไลน์'}
              </span>
            )}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Uptime (ช่วงนี้)</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--cyan)', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
            {u?.uptimePct != null ? `${u.uptimePct}%` : '—'}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>รวมออนไลน์</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>{u ? dur(u.onlineSec) : '—'}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>รวมออฟไลน์ ({u?.incidents.length ?? 0} ครั้ง)</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: (u?.offlineSec ?? 0) > 0 ? '#ef4444' : 'var(--text)', marginTop: 8 }}>{u ? dur(u.offlineSec) : '—'}</div>
        </div>
      </div>

      {/* online/offline events */}
      <div style={cardStyle}>
        <div style={sectionTitle}><Wifi size={13} /> เหตุการณ์ ออนไลน์ / ออฟไลน์ {countLabel(statusEvents.data)}</div>
        <StatusList rows={statusEvents.data} loading={statusEvents.isLoading} />
      </div>

      {/* relay actuation events */}
      <div style={cardStyle}>
        <div style={sectionTitle}><Power size={13} /> เหตุการณ์รีเลย์ เปิด / ปิด {countLabel(relayEvents.data)}</div>
        <RelayTable rows={relayEvents.data} loading={relayEvents.isLoading} />
      </div>

      {/* command audit */}
      <div style={cardStyle}>
        <div style={sectionTitle}><ScrollText size={13} /> ประวัติการสั่งงานจากเว็บ {countLabel(commands.data)}</div>
        <CommandTable rows={commands.data} loading={commands.isLoading} />
      </div>

      {/* board settings audit — who changed what */}
      <div style={cardStyle}>
        <div style={sectionTitle}><PencilLine size={13} /> ประวัติการแก้ไขบอร์ด (ใคร/เมื่อไหร่) {countLabel(audit.data)}</div>
        <AuditTable rows={audit.data} loading={audit.isLoading} />
      </div>
    </section>
  );
}

// Field-name + value formatting for the settings-change audit.
const FIELD_LABEL: Record<string, string> = {
  isActive: 'สถานะใช้งาน', code: 'รหัสบอร์ด', name: 'ชื่อ', zoneId: 'โซน',
  hardware: 'ฮาร์ดแวร์', firmware: 'เฟิร์มแวร์', macAddress: 'MAC', ipAddress: 'IP',
  sensorAggMode: 'โหมดรวมเซนเซอร์', siteId: 'ไซต์',
};
function fmtVal(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'isActive') return v ? 'เปิด' : 'ปิด';
  return String(v);
}
const AUDIT_ACTION: Record<string, { label: string; color: string }> = {
  create: { label: 'สร้าง', color: '#22c55e' },
  update: { label: 'แก้ไข', color: '#22d3ee' },
  delete: { label: 'ลบ', color: '#ef4444' },
};

function AuditTable({ rows, loading }: { rows?: BoardAuditEntry[]; loading: boolean }) {
  if (loading) return <div style={empty}>กำลังโหลด...</div>;
  if (!rows || rows.length === 0) return <div style={empty}>ยังไม่มีประวัติการแก้ไข (บันทึกตั้งแต่ตอนนี้เป็นต้นไป)</div>;
  return (
    <div style={scrollBox}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>เวลา</th><th style={th}>ผู้ใช้</th><th style={th}>การกระทำ</th><th style={th}>เปลี่ยนแปลง</th><th style={th}>IP</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const a = AUDIT_ACTION[r.action] ?? { label: r.action, color: 'var(--dim)' };
            const entries = r.changes ? Object.entries(r.changes) : [];
            return (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td style={{ ...td, color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmt(r.at)}</td>
                <td style={{ ...td, fontWeight: 600 }}>{r.username}</td>
                <td style={td}><span style={{ color: a.color, fontWeight: 700 }}>{a.label}</span></td>
                <td style={td}>
                  {entries.length === 0 ? (
                    <span style={{ color: 'var(--dim)' }}>—</span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {entries.map(([f, ch]) => (
                        <div key={f} style={{ fontSize: 12 }}>
                          <span style={{ color: 'var(--dim)' }}>{FIELD_LABEL[f] ?? f}: </span>
                          {r.action === 'create' ? (
                            <span style={{ color: 'var(--text)' }}>{fmtVal(f, ch.to)}</span>
                          ) : (
                            <>
                              <span style={{ color: 'var(--dim2)', textDecoration: 'line-through' }}>{fmtVal(f, ch.from)}</span>
                              <span style={{ color: 'var(--dim)' }}> → </span>
                              <span style={{ color: 'var(--text)', fontWeight: 600 }}>{fmtVal(f, ch.to)}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ ...td, color: 'var(--dim)', fontFamily: 'monospace', fontSize: 11 }}>{r.ip ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusList({ rows, loading }: { rows?: StatusEventEntry[]; loading: boolean }) {
  if (loading) return <div style={empty}>กำลังโหลด...</div>;
  if (!rows || rows.length === 0) return <div style={empty}>ไม่มีการเปลี่ยนสถานะในช่วงนี้</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
      {rows.map((e) => (
        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 20,
            fontWeight: 700, fontSize: 11, minWidth: 78, justifyContent: 'center',
            background: e.status === 'online' ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
            color: e.status === 'online' ? '#22c55e' : '#ef4444',
          }}>
            {e.status === 'online' ? 'ออนไลน์' : 'ออฟไลน์'}
          </span>
          <span style={{ color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt(e.at)}</span>
        </div>
      ))}
    </div>
  );
}

function RelayTable({ rows, loading }: { rows?: RelayEventEntry[]; loading: boolean }) {
  if (loading) return <div style={empty}>กำลังโหลด...</div>;
  if (!rows || rows.length === 0) return <div style={empty}>ไม่มีเหตุการณ์รีเลย์ในช่วงนี้</div>;
  return (
    <div style={scrollBox}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>เวลา</th><th style={th}>ช่อง</th><th style={th}>สถานะ</th><th style={th}>สาเหตุ</th>
        </tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} style={{ borderTop: '1px solid var(--border-color)' }}>
              <td style={{ ...td, color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmt(e.at)}</td>
              <td style={td}>{e.channel + 1}{e.name ? ` · ${e.name}` : ''}</td>
              <td style={td}><Badge on={e.on} /></td>
              <td style={{ ...td, color: 'var(--dim)' }}>{sourceLabel(e.source)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommandTable({ rows, loading }: { rows?: CommandLogEntry[]; loading: boolean }) {
  if (loading) return <div style={empty}>กำลังโหลด...</div>;
  if (!rows || rows.length === 0) return <div style={empty}>ยังไม่มีการสั่งงานจากเว็บ</div>;
  return (
    <div style={scrollBox}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>เวลา</th><th style={th}>ผู้ใช้</th><th style={th}>การสั่งงาน</th><th style={th}>ช่อง</th><th style={th}>รายละเอียด</th>
        </tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--border-color)' }}>
              <td style={{ ...td, color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmt(c.at)}</td>
              <td style={{ ...td, fontWeight: 600 }}>{c.username}</td>
              <td style={td}>{ACTION_LABEL[c.action] ?? c.action}</td>
              <td style={td}>{c.channel != null ? c.channel + 1 : '—'}</td>
              <td style={{ ...td, color: 'var(--dim)' }}>{commandSummary(c)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
